import { ObjectId } from "@novorender/api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { byProperty, type ObjectDataAccess } from "dataAccess";
import { ModelTreeFilterObjects, ModelTreeNodeData } from "features/modelTree/types";
import { getDescendantsFilteredByParserTypeInfo, makeNode } from "features/modelTree/utils";
import { getFilePathFromObjectPath, isLeafNode, isPdfOrDrawingObject } from "utils/objectData";
import { iterateAsync } from "utils/search";

const MAX_SAMPLE_IDS = 200;
const MAX_CHILDREN_PER_NODE = 500;
const MAX_DEPTH_ITERATIONS = 20;

/**
 * Hard cap on how many children may be accumulated for a single frontier node.
 * Mirrors `maxScan` in features/modelTree/utils.ts: models with huge duplicated
 * sibling lists can otherwise retain millions of references and crash the
 * renderer. Selected objects themselves are injected into the tree separately
 * by selectNode(), so truncating an oversized sibling list here only limits
 * how much of it is browsable, not whether the selection resolves.
 */
const MAX_TOTAL_CHILDREN_PER_NODE = 5000;

/** Max concurrent child listings per depth iteration. */
const MAX_CONCURRENT_LOADS = 5;

/**
 * When objects are selected in the 3D view (e.g. via lasso / area selection),
 * the object tree may not have loaded the subtree containing them.
 *
 * This hook resolves a sample of selected object IDs to their paths, computes
 * ancestor paths, and loads children for any "frontier" tree nodes (nodes
 * whose children haven't been loaded yet) that lie on those ancestor paths.
 * It iterates level by level until the full ancestor chain is present in the
 * tree, so that {@link getGroupStateForIdSet} can correctly propagate
 * selection state up to parent folders (showing "some" instead of "none").
 */
export function useLoadAncestorsForSelection(
    tree: Tree<ModelTreeNodeData>,
    dataAccess: ObjectDataAccess | undefined,
    selectedIdArr: ObjectId[],
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
    fileTypes?: ModelTreeFilterObjects[],
    skipPdfFiles?: boolean,
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">,
): void {
    // Read through a ref, and absent from the dependencies below: a hit only spares a
    // metadata load, so a run that started before discovery finished stays correct.
    const knownPdfObjectIdsRef = useRef(knownPdfObjectIds);
    knownPdfObjectIdsRef.current = knownPdfObjectIds;
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;
    const selectedIdArrRef = useRef(selectedIdArr);
    selectedIdArrRef.current = selectedIdArr;

    useEffect(() => {
        if (!dataAccess || selectedIdArr.length === 0) {
            return;
        }

        // Quick check: any non-leaf nodes without children loaded and not
        // already being loaded by another source (e.g. selectNode)?
        // Include the root node so the hook can bootstrap from parentPath: "".
        const hasFrontierNodes = Object.values(tree).some((n) => !n.isLeaf && !n.children && !n.loading);

        // If not, the tree is fully expanded (or already loading) and there's nothing to do.
        if (!hasFrontierNodes) {
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        (async () => {
            try {
                // 1. Sample selected IDs and resolve to objects to get paths.
                //    First, resolve as many as possible from the existing tree to
                //    avoid redundant network requests (selectNode / expandOrSelectNode
                //    already loads the clicked object into the tree).
                const sampleIds = selectedIdArrRef.current.slice(0, MAX_SAMPLE_IDS);

                const resolvedObjects: { path: string }[] = [];
                const unresolvedIds: string[] = [];

                const treeNodesByIdSnapshot = new Map<number, string>();
                for (const node of Object.values(tree)) {
                    treeNodesByIdSnapshot.set(node.data.object.id, node.path);
                }

                for (const id of sampleIds) {
                    const path = treeNodesByIdSnapshot.get(id);
                    if (path !== undefined) {
                        resolvedObjects.push({ path });
                    } else {
                        unresolvedIds.push(String(id));
                    }
                }

                // Only search for IDs not already in the tree.
                if (unresolvedIds.length > 0) {
                    for await (const obj of dataAccess.findByProperties(byProperty("id", unresolvedIds), { signal })) {
                        resolvedObjects.push({ path: obj.path });
                    }

                    if (signal.aborted) {
                        return;
                    }
                }

                if (signal.aborted || resolvedObjects.length === 0) {
                    return;
                }

                // 2. Compute all unique ancestor paths from selected objects.
                const ancestorPaths = new Set<string>();
                for (const obj of resolvedObjects) {
                    for (const p of getParentPaths(obj.path)) {
                        ancestorPaths.add(p);
                    }
                }

                // 3. Iteratively load children for frontier nodes on ancestor
                //    paths. Each iteration goes one level deeper. We maintain a
                //    lightweight working copy to track which nodes have been
                //    loaded without mutating the real tree yet.
                type LoadResult = {
                    path: string;
                    children: HierarcicalObjectReference[];
                    childIterator?: AsyncIterableIterator<HierarcicalObjectReference>;
                };
                const allResults: LoadResult[] = [];

                // `object` is carried alongside the structure because `getChildren` lists by
                // object, not by path — a frontier node discovered during an earlier depth
                // iteration is not in `tree` yet, so its record is the only place its object is.
                const workingTree: Record<
                    string,
                    { children?: string[]; isLeaf: boolean; object: HierarcicalObjectReference }
                > = {};
                for (const [path, node] of Object.entries(tree)) {
                    workingTree[path] = { children: node.children, isLeaf: node.isLeaf, object: node.data.object };
                }

                for (let depth = 0; depth < MAX_DEPTH_ITERATIONS; depth++) {
                    if (signal.aborted) {
                        return;
                    }

                    // Find frontier nodes that are ancestors of selected objects.
                    // Skip nodes already being loaded by another source (e.g. selectNode)
                    // to avoid issuing duplicate network requests.
                    const frontier: string[] = [];
                    for (const path of ancestorPaths) {
                        const entry = workingTree[path];
                        if (entry && !entry.isLeaf && !entry.children) {
                            // Check the real tree for loading or children state set by selectNode/expandNode.
                            // Both checks are needed: `loading` is set at the start of an async load,
                            // `children` is set when the load completes. Either means the node is being
                            // handled elsewhere, so skip it to prevent concurrent duplicate loads that
                            // double memory consumption.
                            const realNode = tree[path];
                            if (realNode?.loading || realNode?.children) {
                                continue;
                            }
                            frontier.push(path);
                        }
                    }

                    if (frontier.length === 0) {
                        break;
                    }

                    // Load children for frontier nodes with a concurrency cap
                    // to avoid overwhelming the backend / browser connection limits.
                    const results = await pMap(
                        frontier,
                        async (path): Promise<LoadResult | null> => {
                            try {
                                // Reuse an existing iterator stored on the node (from a
                                // previous partial load) so we continue where we left off,
                                // mirroring the pattern used by loadChildren in modelTree/utils.
                                const existingNode = tree[path];
                                // Apply the same file-type filtering as loadChildren:
                                // only filter when fileTypes are active and the path is
                                // above file level. Matched locally for loadChildren's reason.
                                const isFileOrInsideFile = getFilePathFromObjectPath(path);
                                const pathFilter =
                                    fileTypes && !isFileOrInsideFile
                                        ? new Set(getDescendantsFilteredByParserTypeInfo(path, fileTypes))
                                        : undefined;
                                const iterator =
                                    existingNode?.data.childIterator ??
                                    dataAccess.getChildren(path === "" ? undefined : workingTree[path].object, signal);

                                const children: HierarcicalObjectReference[] = [];
                                let done = false;
                                // Budget by distinct paths, not raw records. Models with duplicate-path
                                // records (IFC/RVT exports) can return the same path many times; counting
                                // raw records would accumulate duplicates that get deduped anyway. Seeding
                                // from already-shown children (if any) ensures we don't re-add them.
                                const seenPaths = new Set<string>(existingNode?.children ?? []);

                                while (!done && !signal.aborted && seenPaths.size < MAX_TOTAL_CHILDREN_PER_NODE) {
                                    const [batch, batchDone] = await iterateAsync({
                                        iterator,
                                        count: MAX_CHILDREN_PER_NODE,
                                        abortSignal: signal,
                                    });
                                    for (const child of batch) {
                                        // Empty set means "nothing to narrow by" — see loadChildren.
                                        if (pathFilter?.size && !pathFilter.has(child.path)) {
                                            continue;
                                        }
                                        if (
                                            skipPdfFiles &&
                                            !isFileOrInsideFile &&
                                            (await isPdfOrDrawingObject(child, {
                                                knownPdfObjectIds: knownPdfObjectIdsRef.current,
                                            }))
                                        ) {
                                            continue;
                                        }
                                        // Skip paths we've already seen (duplicates or already-shown children).
                                        if (seenPaths.has(child.path)) {
                                            continue;
                                        }
                                        seenPaths.add(child.path);
                                        children.push(child);
                                    }
                                    done = batchDone;
                                }

                                return {
                                    path,
                                    children,
                                    childIterator: undefined,
                                };
                            } catch {
                                return null;
                            }
                        },
                        { concurrency: MAX_CONCURRENT_LOADS, signal },
                    );

                    if (signal.aborted) {
                        return;
                    }

                    // Apply to working tree for next iteration.
                    let anyNew = false;
                    for (const result of results) {
                        if (!result) {
                            continue;
                        }
                        const entry = workingTree[result.path];
                        if (!entry || entry.children) {
                            continue;
                        }

                        // Dedupe by path: the tree is keyed by path, so several
                        // sibling records sharing one path (common in IFC/RVT
                        // exports) must map to a single child entry. Letting a path
                        // in more than once renders the same node repeatedly and, if
                        // duplicates nest, makes tree traversals blow up. Mirrors the
                        // dedupe in appendDescendants (features/modelTree/utils).
                        // Also validate that each child is actually a direct child of
                        // this parent (not a sibling or unrelated node from search).
                        const childPaths: string[] = [];
                        const seenChildPaths = new Set<string>();
                        const expectedParent = result.path ? `${result.path}/` : "";
                        for (const child of result.children) {
                            if (seenChildPaths.has(child.path)) {
                                continue;
                            }
                            // Validate parent-child relationship
                            if (!child.path.startsWith(expectedParent) || child.path === result.path) {
                                continue;
                            }
                            // Ensure direct child (no additional "/" after parent prefix)
                            const remainder = child.path.slice(expectedParent.length);
                            if (remainder.includes("/")) {
                                continue;
                            }
                            seenChildPaths.add(child.path);
                            if (!workingTree[child.path]) {
                                workingTree[child.path] = {
                                    object: child,
                                    children: undefined,
                                    isLeaf: isLeafNode(child),
                                };
                            }
                            childPaths.push(child.path);
                        }
                        workingTree[result.path] = { ...entry, children: childPaths };
                        allResults.push(result);
                        anyNew = true;
                    }

                    if (!anyNew) {
                        break;
                    }
                }

                if (signal.aborted || allResults.length === 0) {
                    return;
                }

                // 4. Apply all results to the real tree in one update.
                setTreeRef.current((tree) => {
                    const newTree = { ...tree };
                    let changed = false;

                    for (const result of allResults) {
                        const node = newTree[result.path];
                        if (!node || node.children) {
                            continue; // Already loaded by another source.
                        }

                        // Create child nodes, de-duplicating by path (see the
                        // working-tree loop above) so identical-path siblings
                        // collapse to a single child entry. Also validate that
                        // each child is actually a direct child of this parent.
                        const childPaths: string[] = [];
                        const seenChildPaths = new Set<string>();
                        const expectedParent = result.path ? `${result.path}/` : "";
                        for (const child of result.children) {
                            if (seenChildPaths.has(child.path)) {
                                continue;
                            }
                            // Validate parent-child relationship
                            if (!child.path.startsWith(expectedParent) || child.path === result.path) {
                                continue;
                            }
                            // Ensure direct child (no additional "/" after parent prefix)
                            const remainder = child.path.slice(expectedParent.length);
                            if (remainder.includes("/")) {
                                continue;
                            }
                            seenChildPaths.add(child.path);
                            if (!newTree[child.path]) {
                                newTree[child.path] = makeNode(child);
                            }
                            childPaths.push(child.path);
                        }

                        // Update parent with children array and persist the
                        // pagination state needed for any follow-up loads.
                        // Preserve any existing iterator when this load does
                        // not provide a new one, so truncated/aborted loads
                        // do not accidentally mark the node as fully loaded.
                        // Derive hasMoreChildren from the effective iterator
                        // (same as appendDescendants in modelTree/utils) so
                        // the two fields can never get out of sync.
                        const childIterator = result.childIterator ?? node.data.childIterator;
                        newTree[result.path] = {
                            ...node,
                            data: {
                                ...node.data,
                                childIterator,
                            },
                            children: childPaths,
                            hasMoreChildren: Boolean(childIterator),
                        };
                        changed = true;
                    }

                    return changed ? newTree : tree;
                });
            } catch {
                // Silently handle errors – the UI falls back to default
                // behavior (e.g. usePreloadDescendants / descendants arrays).
            }
        })();

        return () => abortController.abort();
        // Re-run when the tree identity changes (nodes added/removed) or when
        // the selection changes. selectedIdArr gets a new reference on every
        // selection mutation in the highlighted reducer, so it reliably
        // triggers re-runs for any selection change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, dataAccess, selectedIdArr, fileTypes, skipPdfFiles]);
}
